import { useSDKConfigStore } from "../stores/sdkConfigStore";
import { getUserCards, updateCard } from "../api/api";
import { dataFetcher } from "./dataFetcher";
import { useCardStatusStore } from "../stores/cardStatusStore";
import { CreditType } from "../api/types";
import { generateCardOperationSign } from "../api/auth";

export interface UpdateCardProviderParams {
  creditType: CreditType;
  signTypedData: (typedData: any) => Promise<string>;
  chainId?: number; // Optional, defaults to current chain
}

export async function updateCardProvider({
  creditType,
  signTypedData,
  chainId,
}: UpdateCardProviderParams): Promise<{ success: boolean; message: string }> {
  const { eoaAddress } = useSDKConfigStore.getState();
  const { userCard, _setUserCard } = useCardStatusStore.getState();

  if (!eoaAddress) {
    return {
      success: false,
      message: "EOA address not found. Please initialize the SDK first.",
    };
  }

  if (!userCard?.cardID) {
    return {
      success: false,
      message: "No card found. Please create a card first.",
    };
  }

  try {
    const eoa = eoaAddress as `0x${string}`;
    const cardID = userCard.cardID;
    const timestamp = Math.floor(Date.now() / 1000);
    const action = "updateCardCreditProviderAddress";
    const targetChainId = chainId || 8453; // Default to Base chain if not provided

    console.log("[updateCardProvider] Step 1: Signing authorization...");
    const { signature } = await generateCardOperationSign(
      eoa,
      targetChainId,
      cardID,
      action,
      timestamp,
      signTypedData
    );

    const authJson = {
      cardID,
      action,
      version: "1",
      timestamp,
      chainID: targetChainId,
      sig: signature,
      userAddr: eoa,
    };

    const encodedAuth = btoa(JSON.stringify(authJson));

    console.log("[updateCardProvider] Step 2: Updating card provider...");
    const updateResult = await updateCard({
      creditProviderAddress: eoaAddress,
      chainId: targetChainId,
      creditType,
      auth: encodedAuth,
    });

    if (!updateResult.success) {
      return {
        success: false,
        message: updateResult.message || "Failed to update card provider",
      };
    }

    console.log("[updateCardProvider] Step 3: Refetching updated data...");
    const userCardsResponse = await getUserCards(eoaAddress);
    console.log({ userCardsResponse });
    const initialUserCard = userCardsResponse.data?.[0] || null;
    _setUserCard(initialUserCard);
    console.log("[updateCardProvider] Card provider updated successfully.");
    return {
      success: true,
      message: "Card provider updated successfully.",
    };
  } catch (err) {
    const message =
      err instanceof Error ? err.message : "An unexpected error occurred.";
    console.error("[updateCardProvider] Error during provider update:", err);
    return {
      success: false,
      message,
    };
  }
}
